import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API, withCredentials: true });

// Global handler for plan-quota 403s — the backend returns a structured detail
// dict `{code:"quota_exceeded", resource, message, current_plan, limit, used}`.
// We surface a single, actionable toast so every mutation route inherits a
// consistent upgrade UX without touching each callsite.
http.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (
      err?.response?.status === 403 &&
      typeof detail === "object" &&
      detail?.code === "quota_exceeded"
    ) {
      toast.error(detail.message || "Quota atteint sur votre formule.", {
        description: `Formule actuelle : ${detail.current_plan}. Rendez-vous dans Paramètres pour changer de formule.`,
        duration: 6000,
      });
    }
    return Promise.reject(err);
  }
);

export const api = {
  dashboard: () => http.get("/dashboard").then((r) => r.data),
  products: () => http.get("/products").then((r) => r.data),
  createProduct: (p, force = false) => http.post("/products", p, { params: { force } }).then((r) => r.data),
  checkProductDuplicates: (p) => http.post("/products/check-duplicates", p).then((r) => r.data),
  cleanupProducts: (params = {}) => http.post("/products/cleanup-duplicates", null, { params }).then((r) => r.data),
  dismissDuplicate: (kind, a_id, b_id) => http.post("/duplicates/dismiss", { kind, a_id, b_id }).then((r) => r.data),
  unmergeProduct: (audit_id) => http.post(`/products/unmerge/${audit_id}`).then((r) => r.data),
  updateProduct: (id, p) => http.put(`/products/${id}`, p).then((r) => r.data),
  deleteProduct: (id) => http.delete(`/products/${id}`).then((r) => r.data),
  productHistory: (id) => http.get(`/products/${id}/history`).then((r) => r.data),
  productAliases: (id) => http.get(`/products/${id}/aliases`).then((r) => r.data),
  addProductAlias: (payload) => http.post("/product-aliases", payload).then((r) => r.data),
  deleteProductAlias: (supplier_key, raw_key) =>
    http.delete("/product-aliases", { params: { supplier_key, raw_key } }).then((r) => r.data),
  mergeProduct: (source_id, into_id) =>
    http.post(`/products/${source_id}/merge`, { into: into_id }).then((r) => r.data),

  suppliers: () => http.get("/suppliers").then((r) => r.data),
  createSupplier: (s) => http.post("/suppliers", s).then((r) => r.data),
  updateSupplier: (id, s) => http.put(`/suppliers/${id}`, s).then((r) => r.data),
  deleteSupplier: (id) => http.delete(`/suppliers/${id}`).then((r) => r.data),

  recipes: () => http.get("/recipes").then((r) => r.data),
  getRecipe: (id) => http.get(`/recipes/${id}`).then((r) => r.data),
  createRecipe: (r) => http.post("/recipes", r).then((res) => res.data),
  updateRecipe: (id, r) => http.put(`/recipes/${id}`, r).then((res) => res.data),
  deleteRecipe: (id) => http.delete(`/recipes/${id}`).then((r) => r.data),

  uploadRecipePhoto: (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    return http.post(`/recipes/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  deleteRecipePhoto: (id) => http.delete(`/recipes/${id}/photo`).then((r) => r.data),
  updateRecipePhotoMeta: (id, meta) => http.put(`/recipes/${id}/photo-meta`, meta).then((r) => r.data),
  seedDemoPhotos: () => http.post("/recipes/seed-demo-photos", {}, { timeout: 120000 }).then((r) => r.data),  // deprecated (btn removed Feb 2026)
  substitutions: (id) => http.get(`/recipes/${id}/substitutions`).then((r) => r.data),
  menu: () => http.get("/menu").then((r) => r.data),
  updateMenu: (payload) => http.put("/menu", payload).then((r) => r.data),
  publishMenu: () => http.post("/menu/publish", {}, { timeout: 15000 }).then((r) => r.data),
  getPublishTask: (id) => http.get(`/menu/publish/${id}`).then((r) => r.data),
  translateMenu: (target) => http.post("/menu/translate", { target }, { timeout: 90000 }).then((r) => r.data),
  regenerateMenuSlug: () => http.post("/menu/regenerate-slug").then((r) => r.data),
  menuQrUrl: (url, size = 8) => `${API}/menu/qr?url=${encodeURIComponent(url)}&size=${size}`,
  advisorChat: (payload) => http.post("/advisor/chat", payload, { timeout: 60000 }).then((r) => r.data),
  generateDescription: (id) => http.post(`/recipes/${id}/generate-description`, {}, { timeout: 45000 }).then((r) => r.data),

  invoices: () => http.get("/invoices").then((r) => r.data),
  getInvoice: (id) => http.get(`/invoices/${id}`).then((r) => r.data),
  createInvoice: (i) => http.post("/invoices", i).then((r) => r.data),
  updateInvoice: (id, i) => http.put(`/invoices/${id}`, i).then((r) => r.data),
  deleteInvoice: (id) => http.delete(`/invoices/${id}`).then((r) => r.data),
  validateInvoice: (id) => http.post(`/invoices/${id}/validate`).then((r) => r.data),
  reanalyzeInvoice: (id) => http.post(`/invoices/${id}/reanalyze`, {}, { timeout: 120000 }).then((r) => r.data),
  reopenInvoice: (id) => http.post(`/invoices/${id}/reopen`).then((r) => r.data),
  patchInvoiceLine: (invoiceId, lineIndex, patch) =>
    http.patch(`/invoices/${invoiceId}/lines/${lineIndex}`, patch).then((r) => r.data),
  dishFlips: (unread_only = false) => http.get("/alerts/dish-flips", { params: { unread_only } }).then((r) => r.data),
  readAllDishFlips: () => http.post("/alerts/dish-flips/read-all").then((r) => r.data),
  saveLineMemory: (supplier_name, raw_text, product_id) =>
    http.post("/invoices/memory/save", { supplier_name, raw_text, product_id }).then((r) => r.data),
  productDetail: (id) => http.get(`/products/${id}/detail`).then((r) => r.data),
  scanInvoice: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/invoices/scan", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    }).then((r) => r.data);
  },
  scanInvoiceAsync: (files) => {
    const fd = new FormData();
    const list = Array.isArray(files) ? files : [files];
    for (const f of list) fd.append("files", f);
    return http.post("/invoices/scan-async", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 30000, // upload only — l'OCR est asynchrone
    }).then((r) => r.data);
  },
  getScanTask: (id) => http.get(`/invoices/scan-tasks/${id}`).then((r) => r.data),
  listActiveScanTasks: () => http.get("/invoices/scan-tasks/active").then((r) => r.data),
  dismissScanTask: (id) => http.delete(`/invoices/scan-tasks/${id}`).then((r) => r.data),

  runSimulation: (payload) => http.post("/simulations/run", payload).then((r) => r.data),
  recommendations: () => http.get("/recommendations").then((r) => r.data),
  negotiation: (pid) => http.get(`/negotiation/${pid}`).then((r) => r.data),
  negotiationSupplier: (sid) => http.get(`/negotiation/dossier/supplier/${sid}`).then((r) => r.data),
  negotiationSuppliersSummary: () => http.get("/negotiation/suppliers/summary").then((r) => r.data),
  comparator: () => http.get("/comparator").then((r) => r.data),
  // Fournisseurs — normalisation & fusion
  resolveSupplier: (payload) => http.post("/suppliers/resolve", payload).then((r) => r.data),
  supplierAliases: (sid) => http.get(`/suppliers/${sid}/aliases`).then((r) => r.data),
  addSupplierAlias: (payload) => http.post("/suppliers/aliases", payload).then((r) => r.data),
  deleteSupplierAlias: (raw_key) => http.delete(`/suppliers/aliases/${encodeURIComponent(raw_key)}`).then((r) => r.data),
  addSupplierSite: (sid, payload) => http.post(`/suppliers/${sid}/sites`, payload).then((r) => r.data),
  deleteSupplierSite: (sid, siteId) => http.delete(`/suppliers/${sid}/sites/${siteId}`).then((r) => r.data),
  mergeSuppliers: (sid, into, siteName) => http.post(`/suppliers/${sid}/merge`, { into, site_name: siteName || null }).then((r) => r.data),
  cleanupSuppliers: () => http.post("/suppliers/cleanup-duplicates").then((r) => r.data),
  unmergeSupplier: (audit_id) => http.post(`/suppliers/unmerge/${audit_id}`).then((r) => r.data),
  supplierLegalLookup: (q) => http.get("/suppliers/legal-lookup", { params: { q }, timeout: 12000 }).then((r) => r.data),
  exportInvoicesCsvUrl: () => `${API}/invoices/export/csv`,
  exportInvoicesPdfUrl: () => `${API}/invoices/export/pdf`,

  weeklySummary: () => http.get("/reports/weekly-summary", { timeout: 60000 }).then((r) => r.data),
  sendWeeklySummary: (to) => http.post("/reports/weekly-summary/send", { to: to || null }, { timeout: 90000 }).then((r) => r.data),

  // Feb 2026 — Par défaut, /demo/load est idempotent côté serveur : si la
  // base démo est déjà présente (docs `source: "demo"` ou user avec
  // `demo_started_at`), le serveur renvoie `{loaded:false,
  // reason:"already-initialized"}` SANS insert ni delete. Pour forcer un
  // rechargement complet, l'appelant doit explicitement appeler
  // `/demo/exit` puis `loadDemo({force:true})`.
  loadDemo: (opts = {}) => http.post(
    `/demo/load${opts.force ? "?force=true" : ""}`,
    {},
    { timeout: 60000 },
  ).then((r) => r.data),
  resetDemo: () => http.post("/demo/reset").then((r) => r.data),
  exitDemo: () => http.post("/demo/exit", {}, { timeout: 30000 }).then((r) => r.data),
  demoSummary: () => http.get("/demo/summary", { timeout: 30000 }).then((r) => r.data),

  todayBrief: () => http.get("/today/brief", { timeout: 30000 }).then((r) => r.data),
  todayAct: (payload) => http.post("/today/act", payload, { timeout: 30000 }).then((r) => r.data),
  leaks: () => http.get("/leaks", { timeout: 20000 }).then((r) => r.data),

  importsList: () => http.get("/imports").then((r) => r.data),
  importsPreview: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return http.post("/imports/preview", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }).then((r) => r.data);
  },
  importsCommit: (payload) => http.post("/imports/commit", payload, { timeout: 120000 }).then((r) => r.data),
  importsDelete: (id) => http.delete(`/imports/${id}`).then((r) => r.data),
  importsSeedDemo: () => http.post("/imports/seed-demo", {}, { timeout: 120000 }).then((r) => r.data),
  importsResetDemo: () => http.post("/imports/reset-demo").then((r) => r.data),
  importsDemoCsvUrl: () => `${API}/imports/demo-csv`,
  importsSuggest: (items) => http.post("/imports/suggest-associations", { items }, { timeout: 30000 }).then((r) => r.data),
  salesAnalytics: () => http.get("/sales/analytics", { timeout: 30000 }).then((r) => r.data),

  tasks: (status) => http.get("/tasks", { params: status ? { status } : {} }).then((r) => r.data),
  updateTask: (id, status) => http.put(`/tasks/${id}`, { status }).then((r) => r.data),

  settings: () => http.get("/settings").then((r) => r.data),
  updateSettings: (s) => http.put("/settings", s).then((r) => r.data),

  authMe: () => http.get("/auth/me").then((r) => r.data),
  authLogin: (email, password) => http.post("/auth/login", { email, password }).then((r) => r.data),
  authRegister: (payload) => http.post("/auth/register", payload).then((r) => r.data),
  authLogout: () => http.post("/auth/logout").then((r) => r.data),
  authGoogleExchange: (session_id) => http.post("/auth/google/session", { session_id }).then((r) => r.data),
  authGoogleLogout: () => http.post("/auth/google/logout").then((r) => r.data),
  authForgot: (email) => http.post("/auth/forgot-password", { email }).then((r) => r.data),
  authReset: (token, password) => http.post("/auth/reset-password", { token, password }).then((r) => r.data),

  billingMe: () => http.get("/billing/me").then((r) => r.data),
  billingCheckout: (lookup_key) =>
    http.post("/billing/checkout", { lookup_key, origin_url: window.location.origin }).then((r) => r.data),
  billingStatus: (session_id) => http.get(`/billing/status/${session_id}`).then((r) => r.data),
  billingCancel: () => http.post("/billing/cancel").then((r) => r.data),
  billingReactivate: () => http.post("/billing/reactivate").then((r) => r.data),
  billingPortal: () =>
    http.post("/billing/portal", { origin_url: window.location.origin }).then((r) => r.data),

  planMe: () => http.get("/plan/me").then((r) => r.data),
};
